import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { CiSelectComponent } from '../../../../profiler-theme';
import { TranslatePipe } from '@ngx-translate/core';
import {
  catchError,
  finalize,
  forkJoin,
  map,
  of,
  Subject,
  switchMap,
} from 'rxjs';
import { IFaehigkeit } from '../../../shared/model/faehigkeit.model';
import {
  ISkillCategory,
  ISkillPatch,
  ISkillUsageConsultant,
} from '../../../shared/model/skill-management.model';
import { FaehigkeitenService } from '../../../services/faehigkeiten.service';
import { MessageService } from '../../../shared/components/message.service';
import { INPUT_LIMITS } from '../../../shared/constants/input-limits';
import {
  compareText,
  hasText,
  problemType,
  sameText,
} from '../skill-catalog-model';
import { SkillIconComponent } from '../skill-icon/skill-icon.component';
import {
  SkillUsageComponent,
  UsageState,
} from '../skill-usage/skill-usage.component';

export interface SkillDeleteRequest {
  skill: IFaehigkeit;
  tasks: number;
  consultants: number;
  hasSimilar: boolean;
}

type SkillFlag =
  | 'isProgrammiersprache'
  | 'isTechnologie'
  | 'isVorgehensmodell'
  | 'isWerkzeug';

interface SkillForm {
  faehigkeit: string;
  faehigkeitEn: string;
  kategorie: string;
  wikipage: string;
  flags: Record<SkillFlag, boolean>;
}

interface SimilarSkill {
  skill: IFaehigkeit;
  consultants: number | null;
}

@Component({
  selector: 'app-skill-detail',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    TranslatePipe,
    SkillIconComponent,
    SkillUsageComponent,
    CiSelectComponent,
  ],
  templateUrl: './skill-detail.component.html',
  styleUrl: './skill-detail.component.scss',
})
export class SkillDetailComponent implements OnChanges {
  private readonly skillService = inject(FaehigkeitenService);
  private readonly messageService = inject(MessageService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  @Input({ required: true }) skill!: IFaehigkeit;
  @Input() allSkills: IFaehigkeit[] = [];
  @Input() categories: ISkillCategory[] = [];
  @Input() assignmentCount = 0;
  @Output() saved = new EventEmitter<IFaehigkeit>();
  @Output() merged = new EventEmitter<string>();
  @Output() deleteRequested = new EventEmitter<SkillDeleteRequest>();

  @ViewChild('similarSection') private similarSection?: ElementRef<HTMLElement>;
  @ViewChild('similarHeading') private similarHeading?: ElementRef<HTMLElement>;

  readonly flagOptions: { flag: SkillFlag; label: string }[] = [
    {
      flag: 'isProgrammiersprache',
      label: 'skillManagement.detail.flagProgrammingLanguage',
    },
    { flag: 'isTechnologie', label: 'skillManagement.detail.flagTechnology' },
    {
      flag: 'isVorgehensmodell',
      label: 'skillManagement.detail.flagProcessModel',
    },
    { flag: 'isWerkzeug', label: 'skillManagement.detail.flagTool' },
  ];

  form!: SkillForm;
  categoryOptions: string[] = [];
  similar: SimilarSkill[] = [];
  mergeSourceId: string | null = null;
  usage: ISkillUsageConsultant[] = [];
  usageState: UsageState = 'loading';
  saving = false;
  merging = false;

  private readonly skillChanges = new Subject<string>();

  constructor() {
    this.skillChanges
      .pipe(
        switchMap((id) => {
          this.usageState = 'loading';
          return this.skillService.usage(id).pipe(
            map((usage) => ({ usage, failed: false })),
            catchError(() =>
              of({ usage: [] as ISkillUsageConsultant[], failed: true }),
            ),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ usage, failed }) => {
        this.usage = usage;
        this.usageState = failed ? 'error' : 'ready';
        this.cdr.markForCheck();
      });
  }

  get limits() {
    return INPUT_LIMITS;
  }

  ngOnChanges(changes: SimpleChanges): void {
    const previous: IFaehigkeit | undefined = changes['skill']?.previousValue;
    if (changes['skill']) {
      this.form = this.formOf(this.skill);
      if (previous?.id !== this.skill.id) {
        this.mergeSourceId = null;
        this.skillChanges.next(this.skill.id!);
      }
    }
    if (changes['skill'] || changes['categories']) {
      this.categoryOptions = this.buildCategoryOptions();
    }
    if (changes['skill'] || changes['allSkills']) {
      this.loadSimilar();
    }
  }

  get germanMissing(): boolean {
    return !hasText(this.form.faehigkeit);
  }

  get englishMissing(): boolean {
    return !hasText(this.form.faehigkeitEn);
  }

  get sameNameInBothLanguages(): boolean {
    return (
      !this.englishMissing &&
      this.form.faehigkeitEn.trim() === this.form.faehigkeit.trim()
    );
  }

  get categoryMoved(): boolean {
    return (
      hasText(this.form.kategorie) &&
      !sameText(this.form.kategorie, this.skill.kategorie)
    );
  }

  get dirty(): boolean {
    const original = this.formOf(this.skill);
    return JSON.stringify(original) !== JSON.stringify(this.form);
  }

  get canSave(): boolean {
    return (
      this.dirty && !this.germanMissing && !this.englishMissing && !this.saving
    );
  }

  get wikiUrl(): string | null {
    const page = this.form.wikipage.trim();
    if (!page) {
      return null;
    }
    const [path, ...anchor] = page.split('#');
    const hash = anchor.length
      ? '#' + encodeURIComponent(anchor.join('#'))
      : '';
    return 'https://de.wikipedia.org/wiki/' + encodeURIComponent(path) + hash;
  }

  get mergeSource(): IFaehigkeit | null {
    return (
      this.similar.find((entry) => entry.skill.id === this.mergeSourceId)
        ?.skill ?? null
    );
  }

  copyGermanToEnglish(): void {
    this.form = { ...this.form, faehigkeitEn: this.form.faehigkeit };
  }

  toggleFlag(flag: SkillFlag): void {
    this.form = {
      ...this.form,
      flags: { ...this.form.flags, [flag]: !this.form.flags[flag] },
    };
  }

  save(): void {
    if (!this.canSave) {
      return;
    }
    this.saving = true;
    const id = this.skill.id!;
    const patch = this.patchOf(this.form);
    const local: IFaehigkeit = { ...this.skill, ...patch };
    this.skillService
      .patch(id, patch)
      .pipe(
        // Gespeichert ist gespeichert; scheitert nur das Nachladen, gilt der lokale Stand.
        switchMap(() =>
          this.skillService.getOneById(id).pipe(catchError(() => of(local))),
        ),
        finalize(() => {
          this.saving = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (updated) => {
          this.messageService.success('skillManagement.detail.saveSuccess');
          this.saved.emit(updated);
        },
        error: (error: unknown) =>
          this.messageService.error(this.saveErrorKey(error)),
      });
  }

  merge(): void {
    const source = this.mergeSource;
    if (!source || this.merging) {
      return;
    }
    this.merging = true;
    this.skillService
      .merge(source.id!, this.skill.id!)
      .pipe(
        finalize(() => {
          this.merging = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.messageService.success('skillManagement.similar.mergeSuccess');
          this.mergeSourceId = null;
          this.merged.emit(source.id!);
          this.skillChanges.next(this.skill.id!);
        },
        error: () =>
          this.messageService.error('skillManagement.similar.mergeError'),
      });
  }

  requestDelete(): void {
    const tasks =
      this.usageState === 'ready'
        ? this.usage.reduce(
            (sum, consultant) =>
              sum +
              consultant.projekte.reduce(
                (inner, project) => inner + project.aufgaben.length,
                0,
              ),
            0,
          )
        : this.assignmentCount;
    this.deleteRequested.emit({
      skill: this.skill,
      tasks,
      consultants: this.usageState === 'ready' ? this.usage.length : 0,
      hasSimilar: this.similar.length > 0,
    });
  }

  scrollToSimilar(): void {
    this.similarSection?.nativeElement.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
    this.similarHeading?.nativeElement.focus({ preventScroll: true });
  }

  hasEnglishName(): boolean {
    return hasText(this.skill.faehigkeitEn);
  }

  private formOf(skill: IFaehigkeit): SkillForm {
    return {
      faehigkeit: skill.faehigkeit ?? '',
      faehigkeitEn: skill.faehigkeitEn ?? '',
      kategorie: this.categoryMatching(skill.kategorie),
      wikipage: skill.wikipage ?? '',
      flags: {
        isProgrammiersprache: !!skill.isProgrammiersprache,
        isTechnologie: !!skill.isTechnologie,
        isVorgehensmodell: !!skill.isVorgehensmodell,
        isWerkzeug: !!skill.isWerkzeug,
      },
    };
  }

  private patchOf(form: SkillForm): ISkillPatch {
    return {
      faehigkeit: form.faehigkeit.trim(),
      faehigkeitEn: form.faehigkeitEn.trim(),
      kategorie: form.kategorie,
      wikipage: form.wikipage.trim(),
      ...form.flags,
    };
  }

  private categoryMatching(kategorie: string | null | undefined): string {
    return (
      this.categories?.find((category) =>
        sameText(category.kategorie, kategorie),
      )?.kategorie ??
      kategorie ??
      ''
    );
  }

  private buildCategoryOptions(): string[] {
    const options = this.categories.map((category) => category.kategorie);
    const current = this.categoryMatching(this.skill.kategorie);
    if (current && !options.some((option) => sameText(option, current))) {
      options.push(current);
    }
    return options.sort(compareText);
  }

  private loadSimilar(): void {
    const matches = this.allSkills.filter(
      (other) =>
        other.id !== this.skill.id &&
        sameText(other.faehigkeit, this.skill.faehigkeit),
    );
    this.similar = matches.map((skill) => ({ skill, consultants: null }));
    if (
      this.mergeSourceId &&
      !matches.some((skill) => skill.id === this.mergeSourceId)
    ) {
      this.mergeSourceId = null;
    }
    if (matches.length === 0) {
      return;
    }
    forkJoin(
      matches.map((skill) =>
        this.skillService.usage(skill.id!).pipe(
          map((usage): number | null => usage.length),
          catchError(() => of(null)),
        ),
      ),
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((counts) => {
        if (
          this.similar.map((entry) => entry.skill.id).join() !==
          matches.map((skill) => skill.id).join()
        ) {
          return;
        }
        this.similar = this.similar.map((entry, index) => ({
          ...entry,
          consultants: counts[index],
        }));
        this.cdr.markForCheck();
      });
  }

  private saveErrorKey(error: unknown): string {
    switch (problemType(error)) {
      case 'entity-name-already-exists':
        return 'skillManagement.detail.duplicate';
      case 'skill-category-unknown':
        return 'skillManagement.detail.categoryUnknown';
      default:
        return error instanceof HttpErrorResponse && error.status === 404
          ? 'skillManagement.detail.notFound'
          : 'skillManagement.detail.saveError';
    }
  }
}
